import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Search } from "lucide-react";

/**
 * Debounced city autocomplete input.
 * Props:
 *  - value: string current city input
 *  - onChange: (city: string) => void
 *  - onSelect: (item: {city, state, count}) => void — fires when suggestion picked
 *  - placeholder, className, dataTestId, iconLeft (bool)
 */
export default function CityAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "City",
  className = "",
  dataTestId = "city-autocomplete",
  iconLeft = false,
  state = "",
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!focused) return;
    const q = value?.trim() || "";
    const timer = setTimeout(() => {
      api.get("/cities/suggest", { params: { q, state, limit: 8 } })
        .then(({ data }) => {
          setItems(data || []);
          setOpen((data || []).length > 0);
        })
        .catch(() => { setItems([]); setOpen(false); });
    }, 180);
    return () => clearTimeout(timer);
  }, [value, state, focused]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (item) => {
    onChange(item.city);
    if (onSelect) onSelect(item);
    setOpen(false);
    setHighlight(-1);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {iconLeft && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />}
      <input
        type="text"
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKey}
        className={`flex h-10 w-full rounded-md border border-input bg-background text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${iconLeft ? "pl-9 pr-3" : "px-3"} py-2`}
        data-testid={dataTestId}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-popover shadow-lg text-sm"
          data-testid={`${dataTestId}-list`}
        >
          {items.map((it, i) => (
            <li
              key={`${it.city}-${it.state}`}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 ${i === highlight ? "bg-accent" : "hover:bg-accent/60"}`}
              data-testid={`${dataTestId}-item-${it.city.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <MapPin size={12} className="text-secondary shrink-0" />
                <span className="truncate">{it.city}</span>
                <span className="text-xs text-muted-foreground truncate">• {it.state}</span>
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                {it.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
